import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import LegalLayout, { Section } from "@/components/legal/LegalLayout";
import { api, API_BASE } from "@/lib/api";

const META = {
  privacy: { title: "Privacy Policy", testid: "privacy-page" },
  terms: { title: "Terms of Service", testid: "terms-page" },
};

// Renders the backend legal JSON (GET /legal/{kind}) — the single source of truth shared with mobile (cc_26).
export default function LegalDoc({ kind }) {
  const meta = META[kind];
  const { data, isLoading, isError } = useQuery({
    queryKey: ["legal", kind],
    queryFn: () => api.legal(kind),
    staleTime: 60 * 60 * 1000,
  });
  const hostedUrl = `${API_BASE}/legal/${kind}.html`;

  return (
    <LegalLayout title={data?.title || meta.title} updated={data?.effective} contactEmail={data?.contact_email} testid={meta.testid}>
      {isLoading && (
        <div className="flex items-center gap-2 text-sm text-bloq-muted" data-testid="legal-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}
      {isError && (
        <p data-testid="legal-error">
          We couldn't load this document right now.{" "}
          <a href={hostedUrl} className="text-bloq-mint hover:underline" data-testid="legal-hosted-link">Open the hosted version</a>.
        </p>
      )}
      {data && (
        <>
          {data.intro && <p data-testid="legal-intro">{data.intro}</p>}
          {(data.sections || []).map((s, i) => (
            <Section key={s.heading || i} heading={s.heading}>
              {(s.items || []).length > 1 ? (
                <ul className="list-disc space-y-2 pl-6">
                  {s.items.map((item, j) => <li key={j}>{item}</li>)}
                </ul>
              ) : (
                (s.items || []).map((item, j) => <p key={j}>{item}</p>)
              )}
            </Section>
          ))}
          <p className="text-xs text-bloq-muted">
            Hosted copy: <a href={hostedUrl} className="text-bloq-mint hover:underline" data-testid="legal-hosted-link">{hostedUrl}</a>
          </p>
        </>
      )}
    </LegalLayout>
  );
}
